import { useState } from "react";
import { motion } from "framer-motion";
import { Slider } from "../ui/slider";
import { useDeck } from "../../lib/deck-context";
import { fmtMoney } from "../../lib/model";
import { Card, Eyebrow } from "../deck/primitives";

const CONTROLS = [
  { k: "partnerCapPct", label: "Partner share of cash invested", min: 0, max: 100, step: 5, fmt: (v) => `${v}% / ${100 - v}% Arqitek` },
  { k: "prefRate", label: "Preferred return (annual, simple)", min: 0, max: 15, step: 0.5, fmt: (v) => `${v}%` },
  { k: "jvSplit", label: "Profit split after pref · Arqitek / Partner", min: 0, max: 100, step: 5, fmt: (v) => `${v}% / ${100 - v}%` },
];

const Tier = ({ n, title, sub, partner, arq, total, max }) => (
  <div data-testid={`dist-tier-${n}`} className="py-5 border-b border-line last:border-0">
    <div className="flex items-baseline justify-between gap-4">
      <div>
        <span className="font-mono text-[10px] tracking-[0.2em] text-arq mr-3">TIER {n}</span>
        <span className="text-ink">{title}</span>
        <div className="text-xs text-steel mt-1">{sub}</div>
      </div>
      <span className="font-display text-2xl text-ink tabular-nums">{fmtMoney(total)}</span>
    </div>
    <div className="mt-3 flex h-3 rounded-full overflow-hidden bg-paper" style={{ width: `${Math.max((total / max) * 100, 2)}%` }}>
      <motion.div className="h-full bg-ink" animate={{ width: `${total ? (partner / total) * 100 : 0}%` }} transition={{ duration: 0.5 }} />
      <motion.div className="h-full bg-arq" animate={{ width: `${total ? (arq / total) * 100 : 0}%` }} transition={{ duration: 0.5 }} />
    </div>
    <div className="mt-2 flex gap-6 text-xs tabular-nums">
      <span className="text-char">Partner {fmtMoney(partner)}</span>
      <span className="text-arq">Arqitek {fmtMoney(arq)}</span>
    </div>
  </div>
);

export const Distribution = ({ rows }) => {
  const { inputs, setInput } = useDeck();
  const [sel, setSel] = useState(3);
  const r = rows[sel];
  const max = Math.max(r.cash, r.prefPaid, r.residual, 1);
  return (
    <Card data-testid="distribution-waterfall" className="mt-6 p-7 lg:p-9">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Eyebrow>Capital-First Distribution Waterfall</Eyebrow>
          <div className="mt-2 font-display text-2xl text-ink">Partner gets cash back + preferred return before profits split</div>
        </div>
        <div className="inline-flex flex-wrap gap-1 rounded-full border border-line bg-white p-1">
          {rows.map((x, i) => (
            <button key={x.key} data-testid={`dist-tab-${x.key}`} onClick={() => setSel(i)} className={`rounded-full px-4 py-1.5 text-xs transition-colors ${sel === i ? "bg-ink text-white" : "text-char hover:bg-paper"}`}>{x.name}</button>
          ))}
        </div>
      </div>
      <div className="mt-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <Tier n={1} title="Return of capital" sub="Every dollar invested comes back first, pro rata" partner={r.partnerCap} arq={r.arqCap} total={r.cash} max={max} />
          <Tier n={2} title={`Preferred return · ${inputs.prefRate}% / yr`} sub={`${r.months} months on invested cash${r.prefPaid < r.prefOwed ? ` · only ${fmtMoney(r.prefPaid)} of ${fmtMoney(r.prefOwed)} owed is covered` : ""}`} partner={r.partnerPref} arq={r.arqPref} total={r.prefPaid} max={max} />
          <Tier n={3} title="Remaining profit split" sub={`${inputs.jvSplit}% Arqitek / ${100 - inputs.jvSplit}% Partner`} partner={r.partnerSplit} arq={r.arqSplit} total={r.residual} max={max} />
        </div>
        <div className="lg:col-span-5 space-y-6">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-ink text-white p-5">
              <div className="text-[11px] text-white/70">Partner takes home</div>
              <div data-testid="dist-partner-total" className="mt-1 font-display text-3xl tabular-nums">{fmtMoney(r.partnerCap + r.partner)}</div>
              <div className="mt-1 text-xs text-white/70 tabular-nums">{fmtMoney(r.partner)} profit · {r.partnerCap ? `${((r.partner / r.partnerCap) * 100).toFixed(0)}% on cash` : "no cash in"}</div>
            </div>
            <div className="rounded-xl bg-arq text-white p-5">
              <div className="text-[11px] text-white/75">Arqitek takes home</div>
              <div data-testid="dist-arq-total" className="mt-1 font-display text-3xl tabular-nums">{fmtMoney(r.arqCap + r.arq)}</div>
              <div className="mt-1 text-xs text-white/75 tabular-nums">{fmtMoney(r.arq)} profit</div>
            </div>
          </div>
          {CONTROLS.map((c) => (
            <div key={c.k}>
              <div className="flex justify-between text-sm mb-3">
                <span className="text-steel">{c.label}</span>
                <span data-testid={`dist-control-value-${c.k}`} className="tabular-nums text-ink font-medium">{c.fmt(inputs[c.k])}</span>
              </div>
              <Slider data-testid={`dist-slider-${c.k}`} min={c.min} max={c.max} step={c.step} value={[inputs[c.k]]} onValueChange={([v]) => setInput(c.k, v)} />
            </div>
          ))}
          <p className="text-xs text-steel leading-relaxed">Illustrative only — JV economics are not established and will be negotiated. Build + Hold profit is largely retained equity, not cash, until a sale.</p>
        </div>
      </div>
    </Card>
  );
};
